import { Client } from '@datocms/cma-client-browser';
import ApiClient from '@src/apiClient/ApiClient';
import { GraphQLClient } from 'graphql-request';
import {
  AIFlowEvaluatedFunction,
  AiFlowRetryCondition,
} from '../types.ts/ai_prompts';
import { AIFlow, AIFlowLogContext } from '@src/aiFlow/AIFlow';
import { AIFlowStep } from '@src/aiFlow/AIFlowStep';
import { resolveFlowStepModels } from './resolveAIModel';
import { loadFlowQueryResult } from '@src/aiFlow/aiFlowQueryHelper';
import { saveAiFlowLogs } from '@src/aiFlow/aiFlowLogSaver';
import { LogManager } from '@src/utility/Logger';

const logger = LogManager.getLogger('repository:AIFlowRepository');

export class AIFlowRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;

  constructor(dClient: Client | ApiClient, gqlClient: GraphQLClient) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
  }

  async load(preset: { title: string; category: string }) {
    const aiFlowResult = await loadFlowQueryResult(this.gqlClient, {
      title: preset.title,
    });
    if (!aiFlowResult) return null;

    const steps = aiFlowResult.steps.map(step => {
      const { legacyModels, resolvedModels } = resolveFlowStepModels(step);

      return new AIFlowStep({
        id: step.id,
        label: step.label,
        systemMessageTemplate: step.systemMessageTemplate,
        userPromptTemplate: step.userPromptTemplate,
        includeMessagesFromStepId: step.overrideMessagesFromStep,
        maxAttempts: step.maxAttempts,
        iterateOver: step.iterateOver,
        models: legacyModels,
        resolvedModels,
        config: {
          temperature: step.temperature,
        },
        resultType: step.resultType,
        stream: step.stream ? 'object' : 'none',
        filter: undefined,
        mapper: undefined,
        reducer: undefined,
        retryCondition: step.retryConditionFunction
          ? (this.evalFunction(
              step.retryConditionFunction,
            ) as AiFlowRetryCondition)
          : true,
      });
    });

    return new AIFlow({
      id: aiFlowResult.id,
      title: aiFlowResult.title,
      category: preset.category,
      steps,
      context: {},
      logEnabled: aiFlowResult.logEnabled,
      onRunLogs: (logs, logContext) => saveAiFlowLogs(this.dClient, logs, logContext),
    });
  }

  private evalFunction(functionString: string) {
    try {
      const Fn = eval(
        functionString.replaceAll('\n', ''),
      ) as AIFlowEvaluatedFunction;
      return Fn;
    } catch (err) {
      logger.error('Error evaluating function', { error: err });
      return null;
    }
  }
}
